import BaseReferenceGenerator from './BaseReferenceGenerator';
import { FieldReferences, MappingReferenceData } from './types';
import { rowConfig as balanceSheetRowConfig } from '../../pages/Financials/balance-sheet/data';
import { NEGATIVE_PREFIX } from '../../pages/Financials/financials-widget-drawer/types';
import { rowConfig as incomeStatementRowConfig } from '../../pages/Financials/income-statement/data';
import { SheetAttributeData } from '../sheet-attribute-manager/models/sheet-attribute-manager.interface';

export enum FINANCIALS_MODEL_TYPE {
  INCOME_STATEMENT = 'income_statement',
  BALANCE_SHEET = 'balance_sheet',
}

export interface FinancialsGeneratedReferences {
  incomeStatementReferences: FieldReferences;
  balanceSheetReferences: FieldReferences;
}

class FinancialsReferenceGenerator extends BaseReferenceGenerator<FinancialsGeneratedReferences> {

  /**
   * @description gets the name of the property bs or is 
   * @param fieldName {string}
   * @returns 
   */
  getFieldModelType(fieldName: string): FINANCIALS_MODEL_TYPE | null {
    const incomeStatementAlias = new Set(incomeStatementRowConfig(false).map(item => item.alias));
    const balanceSheetAlias = new Set(balanceSheetRowConfig(false).map(item => item.alias));

    if (incomeStatementAlias.has(fieldName)) {
      return FINANCIALS_MODEL_TYPE.INCOME_STATEMENT;
    }
    if (balanceSheetAlias.has(fieldName)) {
      return FINANCIALS_MODEL_TYPE.BALANCE_SHEET;
    }

    return null;
  }

  generateMappingReferences(sheetAttributeData: SheetAttributeData): FinancialsGeneratedReferences {
    const incomeStatementReferences: FieldReferences = {};
    const balanceSheetReferences: FieldReferences = {};

    // Iterate over the data property
    Object.keys(sheetAttributeData).forEach(periodIdentifier => {
      const fieldsData = sheetAttributeData[periodIdentifier];

      // For each row property determine if balance sheet or income statement
      Object.keys(fieldsData).forEach(fieldName => {
        const fieldData = fieldsData[fieldName];
        const cleanedFieldName = fieldName.includes(NEGATIVE_PREFIX) 
          ? fieldName.split(NEGATIVE_PREFIX).at(1)! 
          : fieldName;
        const modelType = this.getFieldModelType(cleanedFieldName);

        if (!modelType) {
          return;
        }

        // For each referenceData item (frontend uses camelCase)
        const referenceDataList = fieldData.referenceData || [];

        const mappingReferences: MappingReferenceData[] = [];

        referenceDataList.forEach(refDataItem => {
          const { documentReferenceId } = refDataItem;
          const sheetMappingConfig = refDataItem.sheetMappingConfig || {};

          if (!documentReferenceId) {
            return;
          }

          // For each sheetMappingConfig
          Object.keys(sheetMappingConfig).forEach(sheetName => {
            const sheetConfig = sheetMappingConfig[sheetName];
            const mappedCells = sheetConfig.mappedCells || [];

            if (mappedCells.length === 0) {
              return;
            }

            // Create a mapping reference
            mappingReferences.push({
              document_reference: documentReferenceId,
              field_name: cleanedFieldName,
              sheet_name: sheetName,
              cell_references: mappedCells,
            });
          });
        });

        // Store references in the appropriate structure
        if (modelType === FINANCIALS_MODEL_TYPE.INCOME_STATEMENT) {
          if (!incomeStatementReferences[periodIdentifier]) {
            incomeStatementReferences[periodIdentifier] = {};
          }
          incomeStatementReferences[periodIdentifier][cleanedFieldName] = mappingReferences;
        } else if (modelType === FINANCIALS_MODEL_TYPE.BALANCE_SHEET) {
          if (!balanceSheetReferences[periodIdentifier]) {
            balanceSheetReferences[periodIdentifier] = {};
          }
          balanceSheetReferences[periodIdentifier][cleanedFieldName] = mappingReferences;
        }
      });
    });

    return {
      incomeStatementReferences,
      balanceSheetReferences,
    };
  }

  getReferenceMetadataForCell(
    period: string,
    fieldName: string,
    references: FinancialsGeneratedReferences
  ): MappingReferenceData[] | null {
    // Check income statement references
    const incomeRefs = references.incomeStatementReferences[period]?.[fieldName];
    if (incomeRefs && incomeRefs.length > 0) {
      return incomeRefs;
    }

    // Check balance sheet references
    const balanceRefs = references.balanceSheetReferences[period]?.[fieldName];
    if (balanceRefs && balanceRefs.length > 0) {
      return balanceRefs;
    }

    return null;
  }
}

export default FinancialsReferenceGenerator;

